import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { menusAPI } from '../utils/api';

/**
 * Cache global de menús {ruta: menu_id} (carga única por sesión del browser).
 * Se inicializa la primera vez que un componente usa el hook.
 */
let _menuRoutesCache = null;
let _menuRoutesPromise = null;

const loadMenuRoutes = async () => {
  if (_menuRoutesCache) return _menuRoutesCache;
  if (_menuRoutesPromise) return _menuRoutesPromise;
  _menuRoutesPromise = (async () => {
    try {
      const r = await menusAPI.getAll();
      const list = Array.isArray(r.data) ? r.data : [];
      const map = {};
      list.forEach((m) => {
        if (m.ruta) map[m.ruta] = m.id;
      });
      _menuRoutesCache = map;
      return map;
    } catch {
      _menuRoutesCache = {};
      return {};
    } finally {
      _menuRoutesPromise = null;
    }
  })();
  return _menuRoutesPromise;
};

/**
 * Hook para verificar permisos del usuario actual.
 *
 * Comportamiento:
 *  - Si el usuario es Administrador → bypass (true para todo).
 *  - Si NO es Administrador → verifica `user.permisos` contra el menu_id
 *    resuelto desde la ruta. DEFAULT = DENY (no devuelve true por defecto).
 *
 * @returns { hasPermission, canView, canCreate, canEdit, canDelete, isAdmin, ready }
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const [routesReady, setRoutesReady] = useState(!!_menuRoutesCache);

  useEffect(() => {
    if (_menuRoutesCache) {
      setRoutesReady(true);
      return;
    }
    let active = true;
    loadMenuRoutes().then(() => {
      if (active) setRoutesReady(true);
    });
    return () => { active = false; };
  }, []);

  const isAdmin = () => user?.rol_nombre === 'Administrador';

  /**
   * @param {string} menuRoute - Ruta del menú (ej: '/usuarios')
   * @param {'ver'|'crear'|'editar'|'eliminar'} action
   * @returns {boolean}
   */
  const hasPermission = (menuRoute, action) => {
    if (isAdmin()) return true;
    if (!user || !user.permisos) return false;
    if (!_menuRoutesCache) return false; // aún no resuelto: deny seguro
    const menuId = _menuRoutesCache[menuRoute];
    if (!menuId) return false; // ruta no registrada en BD
    const permiso = user.permisos.find((p) => p.menu_id === menuId);
    return permiso ? !!permiso[action] : false;
  };

  const canView = (menuRoute) => hasPermission(menuRoute, 'ver');
  const canCreate = (menuRoute) => hasPermission(menuRoute, 'crear');
  const canEdit = (menuRoute) => hasPermission(menuRoute, 'editar');
  const canDelete = (menuRoute) => hasPermission(menuRoute, 'eliminar');

  return {
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isAdmin,
    ready: routesReady, // true cuando el cache de rutas terminó de cargar
    // Mantenidos por compat con código existente:
    loading: !routesReady,
    permissions: {},
  };
};

export default usePermissions;
